import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Expand } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/livestock",
)({ component: RouteComponent });

function RouteComponent() {
	const { t, i18n } = useTranslation();
	const permissionsTitleId = useId();
	const locale = i18n.language === "en_US" ? "en-US" : "pt-BR";
	const sections = [
		{
			id: "overview",
			title: t("help.livestockGuide.overviewTitle"),
			intro: t("help.livestockGuide.overviewIntro"),
			steps: [
				t("help.livestockGuide.overviewStep1"),
				t("help.livestockGuide.overviewStep2"),
				t("help.livestockGuide.overviewStep3"),
			],
			note: t("help.livestockGuide.overviewNote"),
			caption: t("help.livestockGuide.overviewCaption"),
			width: 2880,
			height: 1432,
		},
		{
			id: "herds",
			title: t("help.livestockGuide.herdsTitle"),
			intro: t("help.livestockGuide.herdsIntro"),
			steps: [
				t("help.livestockGuide.herdsStep1"),
				t("help.livestockGuide.herdsStep2"),
				t("help.livestockGuide.herdsStep3"),
			],
			note: t("help.livestockGuide.herdsNote"),
			caption: t("help.livestockGuide.herdsCaption"),
			width: 1200,
			height: 1186,
		},
		{
			id: "animals",
			title: t("help.livestockGuide.animalsTitle"),
			intro: t("help.livestockGuide.animalsIntro"),
			steps: [
				t("help.livestockGuide.animalsStep1"),
				t("help.livestockGuide.animalsStep2"),
				t("help.livestockGuide.animalsStep3"),
			],
			note: t("help.livestockGuide.animalsNote"),
			caption: t("help.livestockGuide.animalsCaption"),
			width: 1200,
			height: 986,
		},
		{
			id: "create",
			title: t("help.livestockGuide.createTitle"),
			intro: t("help.livestockGuide.createIntro"),
			steps: [
				t("help.livestockGuide.createStep1"),
				t("help.livestockGuide.createStep2"),
				t("help.livestockGuide.createStep3"),
			],
			note: t("help.livestockGuide.createNote"),
			caption: t("help.livestockGuide.createCaption"),
			width: 1200,
			height: 986,
		},
		{
			id: "detail",
			title: t("help.livestockGuide.detailTitle"),
			intro: t("help.livestockGuide.detailIntro"),
			steps: [
				t("help.livestockGuide.detailStep1"),
				t("help.livestockGuide.detailStep2"),
				t("help.livestockGuide.detailStep3"),
			],
			note: t("help.livestockGuide.detailNote"),
			caption: t("help.livestockGuide.detailCaption"),
			width: 1200,
			height: 986,
		},
		{
			id: "records",
			title: t("help.livestockGuide.recordsTitle"),
			intro: t("help.livestockGuide.recordsIntro"),
			steps: [
				t("help.livestockGuide.recordsStep1"),
				t("help.livestockGuide.recordsStep2"),
				t("help.livestockGuide.recordsStep3"),
			],
			note: t("help.livestockGuide.recordsNote"),
			caption: t("help.livestockGuide.recordsCaption"),
			width: 1024,
			height: 1230,
		},
	];

	return (
		<article className="mx-auto max-w-4xl space-y-8 p-4 text-base-content sm:p-8">
			<Link
				to="/app/help/user-guide"
				className="inline-flex items-center gap-2 text-sm text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
			>
				<ArrowLeft className="size-4" aria-hidden="true" />
				{t("help.userGuide")}
			</Link>
			<header className="max-w-prose space-y-4">
				<h1 className="text-3xl font-bold sm:text-4xl">
					{t("help.guideLivestockTitle")}
				</h1>
				<p className="text-base leading-relaxed">
					{t("help.livestockGuide.intro")}
				</p>
			</header>
			<nav
				aria-label={t("help.livestockGuide.contents")}
				className="border-y border-base-300 py-5"
			>
				<h2 className="mb-3 font-semibold">
					{t("help.livestockGuide.contents")}
				</h2>
				<ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
					{sections.map((section) => (
						<li key={section.id}>
							<a
								href={`#${section.id}`}
								className="text-primary underline underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
							>
								{section.title}
							</a>
						</li>
					))}
				</ul>
			</nav>
			<p className="text-sm text-base-content/70">
				{t("help.livestockGuide.screenshotsNote")}
			</p>
			{sections.map((section) => {
				const src = `/help/livestock/${section.id}-${locale}.webp`;
				return (
					<section
						key={section.id}
						id={section.id}
						aria-labelledby={`${section.id}-title`}
						className="scroll-mt-6 space-y-5 border-b border-base-300 pb-8"
					>
						<div className="max-w-prose space-y-3">
							<h2
								id={`${section.id}-title`}
								className="text-xl font-semibold sm:text-2xl"
							>
								{section.title}
							</h2>
							<p className="leading-relaxed">{section.intro}</p>
							<ol className="list-decimal space-y-3 pl-5 leading-relaxed marker:font-semibold marker:text-primary">
								{section.steps.map((step) => (
									<li key={step} className="pl-1">
										{step}
									</li>
								))}
							</ol>
						</div>
						<figure className="space-y-3">
							<a
								href={src}
								target="_blank"
								rel="noreferrer"
								aria-label={`${t("help.livestockGuide.enlargeImage")}: ${section.title}`}
								className="block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
							>
								<img
									src={src}
									alt={section.caption}
									width={section.width}
									height={section.height}
									loading="lazy"
									decoding="async"
									className="h-auto w-full rounded-lg border border-base-300"
								/>
							</a>
							<figcaption className="space-y-2 text-sm leading-relaxed text-base-content/70">
								<p>{section.caption}</p>
								<a
									href={src}
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-2 text-primary underline underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
								>
									<Expand className="size-4" aria-hidden="true" />
									{t("help.livestockGuide.enlargeImage")}
								</a>
							</figcaption>
						</figure>
						{section.note && (
							<p className="max-w-prose rounded-lg bg-base-100 p-4 text-sm leading-relaxed">
								{section.note}
							</p>
						)}
					</section>
				);
			})}
			<section
				aria-labelledby={permissionsTitleId}
				className="max-w-prose space-y-3"
			>
				<h2 id={permissionsTitleId} className="text-xl font-semibold">
					{t("help.livestockGuide.permissionsTitle")}
				</h2>
				<p className="leading-relaxed">
					{t("help.livestockGuide.permissionsBody")}
				</p>
			</section>
		</article>
	);
}
